import { useState } from 'react';
import { X, Check, Calendar, Clock, Lock, Loader2, Flag, RefreshCw } from 'lucide-react';
import { canPostpone, type CalendarItem } from './calendarApi';

const CAL = {
  card: '#FFFFFF', line: '#E7E6DF', ink: '#14140F', muted: '#6B6B60',
  faint: '#9A998C', green: '#2FA037', greenDeep: '#1C7126', greenSoft: '#EAF6EA',
  surf: '#F4F3EC', red: '#EF4444', redDeep: '#B91C1C', amber: '#F59E0B', blue: '#2A55B5',
};

const MONTHS = ['Jan', 'Feb', 'Mar', 'Apr', 'May', 'Jun', 'Jul', 'Aug', 'Sep', 'Oct', 'Nov', 'Dec'];
function fmtLong(iso?: string | null): string {
  if (!iso) return '—';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '—';
  const wd = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'][d.getDay()];
  return `${wd}, ${d.getDate()} ${MONTHS[d.getMonth()]}`;
}
function toDateInput(iso?: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

const TYPE_HEADER: Record<string, string> = { task: CAL.green, meeting: CAL.blue, followup: CAL.amber };

export interface TaskPopoverProps {
  item: CalendarItem;
  roleName?: string | null;
  /** screen coords (px from viewport top-left) for the anchored card */
  anchor: { x: number; y: number };
  onClose: () => void;
  onComplete: (item: CalendarItem) => Promise<void> | void;
  onPostpone: (item: CalendarItem, scheduledDate: string) => Promise<void> | void;
  onOpenLink?: (item: CalendarItem) => void;
}

/**
 * Click an event → popover. Task → Done / Postpone (Postpone disabled + lock
 * when non-movable for non-managers). Meeting/follow-up → read + deep-link.
 */
export function TaskPopover({ item, roleName, anchor, onClose, onComplete, onPostpone, onOpenLink }: TaskPopoverProps) {
  const [showPostpone, setShowPostpone] = useState(false);
  const [newDate, setNewDate] = useState(toDateInput(item.scheduledDate ?? item.date));
  const [busy, setBusy] = useState<'' | 'done' | 'postpone'>('');
  const locked = item.type === 'task' && item.movable === false;
  const postponeAllowed = canPostpone(item, roleName);
  const isTask = item.type === 'task';
  const done = item.status === 'done';

  // clamp horizontal so the 290px card stays on screen
  const left = Math.min(Math.max(anchor.x - 145, 12), (typeof window !== 'undefined' ? window.innerWidth : 1320) - 302);
  const top = Math.min(anchor.y + 8, (typeof window !== 'undefined' ? window.innerHeight : 880) - 320);

  async function handleComplete() {
    setBusy('done');
    try { await onComplete(item); } finally { setBusy(''); }
  }
  async function handlePostpone() {
    if (!newDate) return;
    setBusy('postpone');
    try { await onPostpone(item, newDate); } finally { setBusy(''); }
  }

  return (
    <>
      {/* click-away */}
      <div onClick={onClose} style={{ position: 'fixed', inset: 0, zIndex: 49 }} />
      <div
        role="dialog"
        aria-label={`${item.type} details`}
        style={{
          position: 'fixed', top, left, width: 290, zIndex: 50, background: CAL.card,
          borderRadius: 16, boxShadow: '0 16px 50px rgba(20,20,15,0.22), 0 0 0 1px rgba(0,0,0,0.05)', overflow: 'hidden',
        }}
      >
        <div style={{ height: 4, background: item.overdue ? CAL.red : TYPE_HEADER[item.type] }} />
        <div style={{ padding: '15px 16px' }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginBottom: 10 }}>
            <span style={{
              display: 'inline-flex', alignItems: 'center', gap: 5,
              background: item.type === 'task' ? CAL.greenSoft : CAL.surf,
              color: item.type === 'task' ? CAL.greenDeep : CAL.muted,
              padding: '3px 8px', borderRadius: 999, fontSize: 10.5, fontWeight: 700, textTransform: 'capitalize',
            }}>
              <span style={{ width: 6, height: 6, borderRadius: '50%', background: TYPE_HEADER[item.type] }} />
              {item.type}
            </span>
            {item.overdue && <span style={{ fontSize: 10.5, fontWeight: 700, color: CAL.redDeep }}>Overdue</span>}
            <div style={{ flex: 1 }} />
            <button aria-label="Close" onClick={onClose} style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 0, lineHeight: 0 }}>
              <X size={16} color={CAL.faint} />
            </button>
          </div>

          <div style={{ fontSize: 16, fontWeight: 700, color: CAL.ink, letterSpacing: -0.2 }}>{item.title}</div>

          {isTask && (
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8, marginBottom: 12, flexWrap: 'wrap' }}>
              {locked && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '3px 8px', borderRadius: 999, fontSize: 11, fontWeight: 700, background: '#EEF1F6', color: '#445063' }}>
                  <Lock size={11} color="#5B6678" /> Non-movable
                </span>
              )}
              {(item.postponedCount ?? 0) > 0 && (
                <span style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: 10.5, fontWeight: 600, color: CAL.faint }}>
                  <RefreshCw size={11} color={CAL.faint} /> Postponed {item.postponedCount}×
                </span>
              )}
            </div>
          )}

          <div style={{ display: 'flex', flexDirection: 'column', gap: 7, margin: '12px 0 14px' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
              <Calendar size={14} color={CAL.green} />
              <span style={{ color: CAL.muted }}>Scheduled</span>
              <b style={{ color: CAL.ink, marginLeft: 'auto' }}>{fmtLong(item.scheduledDate ?? item.date)}</b>
            </div>
            {isTask && (
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, fontSize: 12.5 }}>
                <Clock size={14} color={item.overdue ? CAL.red : CAL.muted} />
                <span style={{ color: item.overdue ? CAL.redDeep : CAL.muted }}>Deadline</span>
                <b style={{ color: item.overdue ? CAL.redDeep : CAL.ink, marginLeft: 'auto' }}>{fmtLong(item.dueDate)}</b>
              </div>
            )}
          </div>

          {isTask ? (
            !showPostpone ? (
              <div style={{ display: 'flex', gap: 8 }}>
                <button
                  onClick={handleComplete}
                  disabled={done || busy !== ''}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10,
                    borderRadius: 10, background: done ? CAL.surf : CAL.green, color: done ? CAL.faint : '#fff',
                    fontSize: 13.5, fontWeight: 700, border: 'none', cursor: done ? 'default' : 'pointer', opacity: busy === 'done' ? 0.7 : 1,
                  }}
                >
                  {busy === 'done' ? <Loader2 size={16} className="animate-spin" /> : <Check size={16} />}
                  {done ? 'Done' : 'Done'}
                </button>
                <button
                  onClick={() => postponeAllowed && setShowPostpone(true)}
                  disabled={!postponeAllowed}
                  title={!postponeAllowed ? 'Locked by manager — non-movable' : ''}
                  style={{
                    flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6, padding: 10, borderRadius: 10,
                    background: !postponeAllowed ? CAL.surf : CAL.card, border: `1px solid ${CAL.line}`,
                    color: !postponeAllowed ? CAL.faint : CAL.ink, fontSize: 13.5, fontWeight: 700,
                    cursor: postponeAllowed ? 'pointer' : 'not-allowed', opacity: !postponeAllowed ? 0.7 : 1,
                  }}
                >
                  {!postponeAllowed ? <Lock size={15} color={CAL.faint} /> : <Calendar size={15} color={CAL.ink} />}
                  Postpone
                </button>
              </div>
            ) : (
              <div style={{ display: 'flex', flexDirection: 'column', gap: 8 }}>
                <label style={{ fontSize: 11.5, fontWeight: 700, color: CAL.muted, textTransform: 'uppercase', letterSpacing: 0.5 }}>
                  New scheduled day
                </label>
                <input
                  type="date"
                  value={newDate}
                  onChange={(e) => setNewDate(e.target.value)}
                  style={{ border: `1px solid ${CAL.line}`, borderRadius: 10, padding: '9px 11px', fontSize: 13.5, color: CAL.ink }}
                />
                <div style={{ fontSize: 11, color: CAL.faint }}>Deadline stays the same.</div>
                <div style={{ display: 'flex', gap: 8 }}>
                  <button onClick={() => setShowPostpone(false)} style={{ flex: 1, padding: 9, borderRadius: 10, border: `1px solid ${CAL.line}`, background: CAL.card, color: CAL.ink, fontSize: 13, fontWeight: 700, cursor: 'pointer' }}>
                    Cancel
                  </button>
                  <button onClick={handlePostpone} disabled={!newDate || busy !== ''} style={{ flex: 1, padding: 9, borderRadius: 10, border: 'none', background: CAL.green, color: '#fff', fontSize: 13, fontWeight: 700, cursor: 'pointer', opacity: busy === 'postpone' ? 0.7 : 1, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}>
                    {busy === 'postpone' && <Loader2 size={14} className="animate-spin" />} Move
                  </button>
                </div>
              </div>
            )
          ) : (
            <button
              onClick={() => onOpenLink?.(item)}
              style={{ width: '100%', padding: 10, borderRadius: 10, border: `1px solid ${CAL.line}`, background: CAL.card, color: CAL.ink, fontSize: 13.5, fontWeight: 700, cursor: 'pointer', display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6 }}
            >
              <Flag size={15} color={CAL.muted} /> {item.type === 'meeting' ? 'Open meeting' : 'Open lead'}
            </button>
          )}

          {locked && !postponeAllowed && (
            <div style={{ fontSize: 11, color: CAL.faint, marginTop: 9, textAlign: 'center' }}>Non-movable — assigned by manager</div>
          )}
        </div>
      </div>
    </>
  );
}
